// src/constants/upload.constants.ts - Comprehensive upload configuration
import { UploadConfig } from '../types/upload.types';

export const UPLOAD_CONFIG: UploadConfig = {
  apiBaseUrl: import.meta.env.VITE_CUSTOMER_API_BASE_URL || 'http://localhost:3000',
  maxRetries: 3,
  retryDelay: 1000, // ms
  concurrentUploads: 32, // Sequential uploads to maintain order
  chunkSize: 5 * 1024 * 1024, // 5MB chunks
};

export const THUMBNAIL_EXPIRY_MARGIN_MS = 60 * 60 * 1000; // 1 hour

export const S3_CONFIG = {
  bucket: import.meta.env.VITE_S3_BUCKET_NAME || 'ar-trial',
  folderPrefix: 'UserUpload',
  draftSuffix: 'draft', // Used for draft folder naming
} as const;

export const UPLOAD_ENDPOINTS = {
  credentials: '/api/uploads/credentials',
  complete: '/api/uploads/complete',
  status: '/api/uploads/:uploadId/status',
  renameDraftToFinal: '/api/uploads/rename-draft-to-final',
  renameStatus: '/api/uploads/rename-status',
  markReadyForReconstruction: '/api/uploads/mark-ready-for-reconstruction',
} as const;

export const UPLOAD_TIMEOUTS = {
  credentials: 10000, // 10 seconds
  fileUpload: 300000, // 5 minutes per file
  completion: 30000,  // 30 seconds
  renameOperation: 60000, // 1 minute for rename operations
} as const;

export const PROGRESS_STAGES = {
  GETTING_CREDENTIALS: 5,
  UPLOADING_FILES: 65, // Reduced to make room for copying and renaming
  UPLOADING_METADATA: 70, // Stage for metadata files
  COPYING_TO_FINAL: 85, // Copying files from draft to final folder
  RENAMING_FOLDER: 95, // Renaming draft folder to final
  COMPLETED: 100,
} as const;

export const ERROR_MESSAGES = {
  NO_AUTH_TOKEN: 'Authentication token not found. Please log in again.',
  NO_USER_DATA: 'User data not found. Please log in again.',
  NO_USER_ID: 'User ID not found in user data.',
  NO_IMAGES: 'No images to upload',
  NO_SCENE: 'Scene information is required for upload',
  UPLOAD_CANCELLED: 'Upload cancelled',
  CREDENTIALS_FAILED: 'Failed to get upload credentials',
  S3_CLIENT_NOT_INITIALIZED: 'S3 client not initialized',
  METADATA_GENERATION_FAILED: 'Failed to generate metadata files',
  RENAME_OPERATION_FAILED: 'Failed to rename draft folder to final',
  COPY_OPERATION_FAILED: 'Failed to copy files to final folder',
} as const;

export const FOLDER_NAME_PATTERN = {
  template: '{userId}/{prefix}_{sessionUUID}_{timestamp}',
  draftTemplate: '{userId}/{prefix}_{sessionUUID}_{timestamp}_draft',
  dateFormat: 'YYYY-MM-DD_HH-mm-ss',
  imageTimestampOffset: 1000, // 1 second in milliseconds between images
} as const;

export const METADATA_FILES = {
  SESSION_METADATA: 'sessionMetadata.json',
  SCAN_METADATA: 'scanMetadata.json',
} as const;

// Default focal length for images (matches iOS default)
export const DEFAULT_FOCAL_LENGTH_35MM = 5.1;

// Image filename pattern
export const IMAGE_FILENAME_PATTERN = 'photo_{timestamp}.{extension}';

// Supported image extensions
export const SUPPORTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.webp'] as const;

// Debug configuration helper
export const DEBUG_CONFIG = {
  logS3Operations: import.meta.env.VITE_DEBUG_S3 === 'true',
  logMetadataOperations: import.meta.env.VITE_DEBUG_METADATA === 'true',
  logReconstructionData: import.meta.env.VITE_DEBUG_RECONSTRUCTION === 'true',
} as const;